import { ROOM_COLUMN_WIDTH_PX, TIMELINE_LANE_HEIGHT_PX } from '../lib/timeline';
import { type RoomRowData } from '../lib/types';
import { ClassScheduleBlock } from './class-schedule-block';
import { TimelineGridLines } from './timeline-grid-lines';

interface RoomRowProps {
  row: RoomRowData;
  rowIndex: number;
}

interface RoomOverviewTimelineRowProps extends RoomRowProps {
  hourWidthPx: number;
  timelineWidthPx: number;
}

function getRowLayout(row: RoomRowData) {
  const laneCount = Math.max(row.lanes.length, 1);
  const rowHeight = laneCount * TIMELINE_LANE_HEIGHT_PX;
  return { laneCount, rowHeight };
}

function getRowBgClass(rowIndex: number) {
  return rowIndex % 2 === 1 ? 'bg-muted' : 'bg-background';
}

export function RoomOverviewRoomCell({ row, rowIndex }: RoomRowProps) {
  const { rowHeight } = getRowLayout(row);
  const rowBgClass = getRowBgClass(rowIndex);

  return (
    <div
      className={`flex shrink-0 flex-col justify-center border-b border-r-2 border-border/70 px-3 py-2 ${rowBgClass}`}
      style={{ width: ROOM_COLUMN_WIDTH_PX, minHeight: rowHeight, height: rowHeight }}
    >
      <div className='truncate text-xs text-muted-foreground'>
        Classes: {row.classCount}
      </div>
      <div className='truncate text-sm font-medium'>{row.roomName}</div>
    </div>
  );
}

export function RoomOverviewTimelineRow({
  row,
  rowIndex,
  hourWidthPx,
  timelineWidthPx,
}: RoomOverviewTimelineRowProps) {
  const { rowHeight } = getRowLayout(row);
  const hasEvents = row.lanes.some((lane) => lane.length > 0);
  const rowBgClass = getRowBgClass(rowIndex);

  return (
    <div
      className={`relative shrink-0 border-b border-border/70 ${rowBgClass}`}
      style={{
        width: timelineWidthPx,
        minWidth: timelineWidthPx,
        height: rowHeight,
      }}
    >
      <TimelineGridLines
        hourWidthPx={hourWidthPx}
        timelineWidthPx={timelineWidthPx}
      />

      {!hasEvents && (
        <div className='absolute inset-0 flex items-center px-2 text-sm'>
          No classes
        </div>
      )}

      {row.lanes.map((lane, laneIndex) => (
        <div
          key={laneIndex}
          className='relative'
          style={{ height: TIMELINE_LANE_HEIGHT_PX }}
        >
          {lane.map((positioned) => (
            <ClassScheduleBlock
              key={`${positioned.event.classId}-${positioned.event.startMinutes}-${positioned.event.endMinutes}`}
              event={positioned.event}
              leftPx={positioned.leftPx}
              widthPx={positioned.widthPx}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
